import axios from "axios";
import { toast } from "sonner";

import type { TokenResponse, UserRegisterRequest } from "@/types/auth";

// Базовый клиент для auth запросов
const authApiClient = axios.create({
  baseURL: "/api",
  timeout: 15000,
  headers: {
    "Content-Type": "application/json",
  },
});

class AuthService {
  private tokenKey = "auth_token";

  async login(email: string, password: string): Promise<void> {
    try {
      const response = await authApiClient.post<TokenResponse>(
        "/v1/auth/login",
        { email, password },
        { withCredentials: true } // Для получения refresh token cookie
      );
      this.saveTokens(response.data);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async register(data: UserRegisterRequest): Promise<void> {
    try {
      await authApiClient.post("/v1/auth/register", data);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async logout(): Promise<void> {
    localStorage.removeItem(this.tokenKey);

    try {
      await authApiClient.post("/v1/auth/logout", null, { withCredentials: true });
    } catch {
      // Локальный выход не должен зависеть от доступности/валидности backend-сессии.
    }
  }

  async refreshToken(options: { silent?: boolean } = {}): Promise<boolean> {
    try {
      // Refresh token передаётся через httpOnly cookie автоматически
      const response = await authApiClient.post<TokenResponse>(
        "/v1/auth/refresh-token",
        null,
        { withCredentials: true } // Важно для отправки cookies
      );

      this.saveTokens(response.data);
      return true;
    } catch (error: unknown) {
      const axiosError = error as { response?: { status: number; data?: string } };

      if (!options.silent && axiosError.response?.status === 401) {
        toast.error("Сессия истекла. Пожалуйста, войдите снова.");
      }
      localStorage.removeItem(this.tokenKey);
      return false;
    }
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  private saveTokens(data: TokenResponse): void {
    localStorage.setItem(this.tokenKey, data.access_token);
  }

  private handleError(error: unknown): Error {
    const axiosError = error as {
      response?: {
        status: number;
        data?: unknown;
      };
      request?: unknown;
    };

    if (axiosError.response) {
      const { status, data } = axiosError.response;

      if (typeof data === "string" && data.trim()) {
        return new Error(data);
      }

      if (data && typeof data === "object") {
        const errorData = data as {
          detail?: string;
          message?: string;
          error?: string;
          errors?: Record<string, string>;
        };

        const backendMessage =
          errorData.detail || errorData.message || errorData.error;

        if (backendMessage) {
          const e = new Error(backendMessage) as Error & {
            fieldErrors?: Record<string, string>;
          };
          if (errorData.errors) e.fieldErrors = errorData.errors;
          return e;
        }
      }

      if (status === 401) {
        return new Error("Неверный email или пароль");
      }

      return new Error("Ошибка при выполнении запроса");
    }

    if (axiosError.request) {
      return new Error("Сервер не отвечает. Проверьте интернет-соединение.");
    }

    return new Error("Произошла ошибка. Попробуйте снова позже.");
  }

}

export const authService = new AuthService();
